/**
 * Job Toast Components
 *
 * Custom toast components for sector research job notifications.
 * Displays rich, interactive toasts with metrics, actions, and progress tracking.
 *
 * @module components/notifications/JobToasts
 */

import { memo } from 'react';
import { CheckCircle2, XCircle, Clock, TrendingUp, BarChart3, Target, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { formatDuration, formatCompactNumber, ReportMetrics, JobError, JobProgress } from '../../lib/toast';

// ============================================================================
// JOB START TOAST
// ============================================================================

interface JobStartToastProps {
  jobId: string;
  sector: string;
  estimatedMinutes: number;
  onViewStatus?: () => void;
  onDismiss: () => void;
}

/**
 * Toast notification displayed when a job starts
 *
 * Shows: Sector name, estimated time, and optional "View Status" action
 * Auto-dismisses after 4 seconds
 */
export const JobStartToast = memo(function JobStartToast({
  sector,
  estimatedMinutes,
  onViewStatus,
  onDismiss,
}: JobStartToastProps) {
  return (
    <div
      className="bg-card border border-border rounded-lg shadow-lg p-4 pr-10 min-w-[320px] max-w-[420px] relative"
      role="status"
      aria-live="polite"
    >
      {/* Dismiss Button */}
      <button
        onClick={onDismiss}
        className="absolute top-1 right-1 text-muted-foreground hover:text-foreground transition-colors p-2 rounded-md hover:bg-accent min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Dismiss notification"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Clock className="h-5 w-5 text-primary" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="text-sm font-semibold text-foreground">
              Report Queued: {sector}
            </h4>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            Estimated: {estimatedMinutes} {estimatedMinutes === 1 ? 'minute' : 'minutes'}
          </p>

          {/* Action */}
          {onViewStatus && (
            <Button
              onClick={onViewStatus}
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs font-medium"
            >
              View Status
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// JOB COMPLETE TOAST
// ============================================================================

interface JobCompleteToastProps {
  reportId: string;
  sector: string;
  metrics: ReportMetrics;
  onQuickPreview?: () => void;
  onViewReport?: () => void;
  onDismiss: () => void;
}

/**
 * Toast notification displayed when a job completes successfully
 *
 * Shows: Celebration, sector name, key metrics, and action buttons
 * Auto-dismisses after 12 seconds
 */
export const JobCompleteToast = memo(function JobCompleteToast({
  sector,
  metrics,
  onQuickPreview,
  onViewReport,
  onDismiss,
}: JobCompleteToastProps) {
  return (
    <div
      className="bg-card border border-border rounded-lg shadow-lg p-4 pr-10 min-w-[360px] max-w-[480px] relative"
      role="status"
      aria-live="polite"
    >
      {/* Dismiss Button */}
      <button
        onClick={onDismiss}
        className="absolute top-1 right-1 text-muted-foreground hover:text-foreground transition-colors p-2 rounded-md hover:bg-accent min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Dismiss notification"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex items-start gap-3">
        {/* Success Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="text-sm font-semibold text-foreground">
              {sector} Report Ready
            </h4>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-3 gap-3 mb-3 mt-2">
            {/* Market Size */}
            {metrics.marketSize && (
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground truncate">Market</div>
                  <div className="text-sm font-semibold text-foreground truncate">
                    {metrics.marketSize}
                  </div>
                </div>
              </div>
            )}

            {/* Growth Rate */}
            {metrics.growthRate && (
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground truncate">Growth</div>
                  <div className="text-sm font-semibold text-foreground truncate">
                    {metrics.growthRate}
                  </div>
                </div>
              </div>
            )}

            {/* Target Count */}
            {metrics.targetCount && (
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground truncate">Targets</div>
                  <div className="text-sm font-semibold text-foreground truncate">
                    {formatCompactNumber(metrics.targetCount)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Key Insight */}
          {metrics.keyInsight && (
            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
              {metrics.keyInsight}
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2">
            {onQuickPreview && (
              <Button
                onClick={onQuickPreview}
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs font-medium"
              >
                Quick Preview
              </Button>
            )}
            {onViewReport && (
              <Button
                onClick={onViewReport}
                variant="default"
                size="sm"
                className="h-8 px-3 text-xs font-medium"
              >
                View Report
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// JOB ERROR TOAST
// ============================================================================

interface JobErrorToastProps {
  jobId: string;
  sector: string;
  error: JobError;
  onRetry?: () => void;
  onEditQuery?: () => void;
  onDismiss: () => void;
}

/**
 * Toast notification displayed when a job fails
 *
 * Shows: Error icon, sector name, error message, and contextual actions
 * Does NOT auto-dismiss - user must explicitly dismiss
 */
export const JobErrorToast = memo(function JobErrorToast({
  sector,
  error,
  onRetry,
  onEditQuery,
  onDismiss,
}: JobErrorToastProps) {
  return (
    <div
      className="bg-card border border-destructive/50 rounded-lg shadow-lg p-4 pr-10 min-w-[320px] max-w-[420px] relative"
      role="alert"
      aria-live="assertive"
    >
      {/* Dismiss Button */}
      <button
        onClick={onDismiss}
        className="absolute top-1 right-1 text-muted-foreground hover:text-foreground transition-colors p-2 rounded-md hover:bg-accent min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Dismiss notification"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex items-start gap-3">
        {/* Error Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
            <XCircle className="h-5 w-5 text-destructive" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="text-sm font-semibold text-foreground">
              {sector} Report Failed
            </h4>
          </div>

          <p className="text-sm text-muted-foreground mb-3">
            {error.message}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {error.retryable && onRetry && (
              <Button
                onClick={onRetry}
                variant="default"
                size="sm"
                className="h-8 px-3 text-xs font-medium"
              >
                Retry
              </Button>
            )}
            {onEditQuery && (
              <Button
                onClick={onEditQuery}
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs font-medium"
              >
                Edit Query
              </Button>
            )}
            {!error.retryable && !onEditQuery && (
              <Button
                onClick={onDismiss}
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs font-medium"
              >
                OK
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// JOB PROGRESS TOAST
// ============================================================================

interface JobProgressToastProps {
  jobId: string;
  sector: string;
  progress: JobProgress;
  onCancel?: () => void;
  onDismiss: () => void;
}

/**
 * Toast notification displayed during job execution
 *
 * Shows: Progress bar, sector name, status message, estimated time remaining
 * Does NOT auto-dismiss - stays visible until job completes
 */
export const JobProgressToast = memo(function JobProgressToast({
  sector,
  progress,
  onCancel,
  onDismiss,
}: JobProgressToastProps) {
  return (
    <div
      className="bg-card border border-border rounded-lg shadow-lg p-4 pr-10 min-w-[320px] max-w-[420px] relative"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Dismiss Button */}
      <button
        onClick={onDismiss}
        className="absolute top-1 right-1 text-muted-foreground hover:text-foreground transition-colors p-2 rounded-md hover:bg-accent min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Dismiss notification"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-primary animate-pulse" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="text-sm font-semibold text-foreground">
              Generating {sector} Report
            </h4>
            <span className="text-xs font-medium text-muted-foreground">
              {Math.round(progress.percent)}%
            </span>
          </div>

          {/* Progress Bar */}
          <Progress value={progress.percent} className="h-2 mb-2" />

          {/* Status Message */}
          <p className="text-xs text-muted-foreground mb-1">
            {progress.message}
          </p>

          {/* Estimated Time */}
          {progress.estimatedSeconds !== undefined && progress.estimatedSeconds > 0 && (
            <p className="text-xs text-muted-foreground mb-3">
              About {formatDuration(progress.estimatedSeconds)} remaining
            </p>
          )}

          {/* Cancel Button */}
          {onCancel && (
            <Button
              onClick={onCancel}
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs font-medium"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// MOBILE RESPONSIVE WRAPPER
// ============================================================================

/**
 * Wrapper component that applies mobile-specific styling to toasts
 *
 * On mobile (< 640px):
 * - Full width toasts
 * - Larger touch targets
 * - Bottom positioning (not blocking nav)
 *
 * @internal
 */
export function MobileToastWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="sm:max-w-[420px] max-w-full w-full sm:w-auto">
      {children}
    </div>
  );
}
